/**
 * The clip creator's domain model.
 *
 * A **ToolProject** is the unit the user works in: a name, one or more
 * source videos, and an ordered list of clips cut from any of them. The
 * order matters — it's the order clips export in and the order the
 * "single video" join follows.
 *
 * Nothing here touches React or the network; the persistence layer lives
 * in ``projectStore.ts`` and the API calls in ``@/api/endpoints``.
 */

export interface ClipColor {
  border: string
  fill: string
}

/** Sequential palette for clips — 6 hues that stay legible over the
 * filmstrip. Derived from the clip id, never persisted, so the rotation
 * is stable across reloads and unaffected by reordering. Typed as a
 * non-empty tuple so index math needs no non-null assertion. */
export const CLIP_COLORS: readonly [ClipColor, ...ClipColor[]] = [
  { border: '#FF6B1A', fill: 'rgba(255,107,26,0.22)' },
  { border: '#60A5FA', fill: 'rgba(96,165,250,0.22)' },
  { border: '#4ADE80', fill: 'rgba(74,222,128,0.22)' },
  { border: '#A78BFA', fill: 'rgba(167,139,250,0.22)' },
  { border: '#FACC15', fill: 'rgba(250,204,21,0.22)' },
  { border: '#F87171', fill: 'rgba(248,113,113,0.22)' },
]

/** Shortest clip we let the user create, and the minimum gap a marker
 * drag keeps from the opposite marker. Stops a stray double-click from
 * producing a zero-length cut. */
export const MIN_CLIP_S = 1

export interface ClipDownload {
  url: string
  filename: string
}

/** A source video attached to a project. */
export interface ProjectVideo {
  /** Client-side id — stable across renames and reorders. */
  id: string
  /** Server-side filesystem path; what ``/cut-video`` cuts from. */
  tempPath: string
  /** Relative ``/output`` path. Run through ``resolveDownloadUrl`` before
   * putting it on a ``<video src>`` — production serves it from the API
   * origin. */
  videoUrl: string
  /**
   * Small, densely-keyframed copy used by the *player* only — cutting and
   * export always read ``tempPath``. Null on videos prepared before
   * proxies existed, in which case the player falls back to ``videoUrl``
   * and scrubbing is as heavy as it used to be.
   */
  proxyUrl: string | null
  /** Original file name. An implementation detail of the disk, surfaced
   * only in the "add video" picker — the user names the *project*. */
  filename: string
  origin: 'upload' | 'youtube'
  /** 0 until the browser reports it (``loadedmetadata``); the imports
   * endpoint doesn't probe. */
  duration: number
  width: number | null
  height: number | null
  fileSize: number | null
}

/**
 * A frame held still for a while, inside a clip.
 *
 * Not a clip of its own and not a tag: the decision (08/08/2026) is that a
 * freeze **stretches the clip that contains it**, so the timeline shows one
 * bar with a hatched stretch in the clip's own colour rather than a
 * separate block you'd have to trace back to its source.
 *
 * That's also what breaks the oldest assumption in this model: a clip's
 * length stops being ``end - start``. See {@link clipDuration}.
 */
export interface FreezeFrame {
  id: string
  /** Source second the held frame is lifted from — inside the clip's own
   * bounds, in the source file's clock like ``start``/``end``. */
  at: number
  /** How long it's held, in **project** seconds. No source footage is
   * consumed: this time is added to the clip. */
  duration: number
}

/** Length of a freshly inserted freeze, and the range the inspector and the
 * timeline drag allow. */
export const DEFAULT_FREEZE_S = 1.5
export const MIN_FREEZE_S = 0.1
export const MAX_FREEZE_S = 30

/**
 * Speed limits.
 *
 * The ceiling is ffmpeg's, not a taste call: ``atempo`` takes 0.5–2.0 per
 * instance, so anything outside that has to be chained, and two links cover
 * 0.25×–4×. Wider would mean three links and audio that has stopped being
 * worth listening to.
 */
export const MIN_SPEED = 0.25
export const MAX_SPEED = 4
/** What the inspector offers as one click. 1 is always in the list so
 * "back to normal" never needs a drag. */
export const SPEED_PRESETS = [0.25, 0.5, 1, 1.5, 2, 4] as const

/** A marked range inside one of the project's videos. */
export interface ProjectClip {
  id: number
  /** Which {@link ProjectVideo} this range belongs to. */
  videoId: string
  start: number
  end: number
  /** User-given name. Empty means "use the positional default", which is
   * why this is `''` and not optional — the field is rewritten in place
   * whenever the user edits it. */
  name: string
  /** Populated after a successful export; dropped the moment the clip's
   * bounds change, because the file on disk no longer matches. */
  download: ClipDownload | null
  /**
   * Kept in the list but out of the cut: excluded from "só os cortes" and
   * from the export, still drawn (dimmed) in "vídeos completos" so there is
   * somewhere to click to bring it back. For holding a doubtful take while
   * you decide, instead of deleting and re-marking it.
   */
  hidden: boolean
  /**
   * Playback rate for this clip. 1 is the source's own speed; 2 plays it
   * twice as fast and takes half as long on the timeline.
   *
   * A **clip** property rather than a free range on the timebase, and that
   * is deliberate. A speed range defined in output seconds is circular —
   * speeding a stretch up shortens the axis the stretch is measured on — so
   * every NLE anchors speed to the source clock instead. Here the clip
   * already *is* a range of source, so speeding part of a video up means
   * marking that part as its own clip, which is the razor-then-retime move
   * from any timeline editor.
   */
  speed: number
  /** Held frames inside this clip, in source order. */
  freezes: FreezeFrame[]
}

/**
 * A caption drawn over the video for part of the timeline.
 *
 * Every geometric field is a **fraction of the frame**, never a pixel.
 * What you position on screen is a 720p proxy inside a box of whatever
 * size the layout gives it, and what gets burned is the 4K master — a
 * pixel offset would land somewhere else in each. Fractions survive both,
 * and survive the window being resized mid-edit.
 */
export interface TextOverlay {
  id: string
  text: string
  /** Project seconds — the same axis the timeline and playhead use. */
  start: number
  end: number
  /** Centre of the text box, 0–1 across the frame. */
  x: number
  y: number
  /** Height of one line as a fraction of frame height. 0.06 ≈ the size a
   * caption usually wants on a Short. */
  size: number
  /** Font *family* = the file stem in the backend's ``data/fonts/``. The
   * browser and ffmpeg resolve the same file from it. */
  fontFamily: string
  color: string
  /** Drawn behind the text so it stays readable over bright footage. */
  background: 'none' | 'shadow' | 'box'
  /** Shadow spread, in `em` — relative to the font, like every other
   * measurement here, so it looks the same on the preview and on the 4K
   * burn-in. Only read when ``background === 'shadow'``. */
  shadowSize: number
  shadowColor: string
  /** How the caption enters and leaves. See {@link TextTransition}. */
  transitionIn: TextTransition
  transitionOut: TextTransition
}

/**
 * A caption's entrance or exit.
 *
 * The vocabulary is closed to **opacity + translation**, and the boundary
 * is drawn by the burn-in rather than by taste. A caption is one
 * frame-sized PNG, and the two things ffmpeg can vary per frame on it
 * cheaply are its alpha (the ``fade`` filter) and where ``overlay`` puts it
 * — both take expressions in ``t``.
 *
 * Scale is *also* affine, and it is deliberately not here: ``scale``
 * evaluates its expressions once, not per frame, so a growing caption would
 * need ``zoompan`` (slow at 4K, and awkward to anchor) or one PNG per
 * frame. That is the cost step the design set out to avoid — see the
 * clip-creator doc.
 *
 * Inside the family, one specification drives two renderers: the editor
 * computes the same opacity and offset in CSS from the playhead, so the
 * preview cannot drift from the export.
 */
export interface TextTransition {
  kind: TransitionKind
  /** Seconds. Clamped at render time to half the caption's span, so a 0,4 s
   * entrance on a 0,5 s caption doesn't overlap its own exit. */
  duration: number
}

export type TransitionKind =
  | 'none'
  | 'fade'
  | 'slide-up'
  | 'slide-down'
  | 'slide-left'
  | 'slide-right'

export const TRANSITION_OPTIONS: ReadonlyArray<{ value: TransitionKind; label: string }> = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide-up', label: 'Subindo' },
  { value: 'slide-down', label: 'Descendo' },
  { value: 'slide-left', label: 'Da direita' },
  { value: 'slide-right', label: 'Da esquerda' },
]

export const DEFAULT_TRANSITION: TextTransition = { kind: 'none', duration: 0.4 }
export const MAX_TRANSITION_S = 2

/** How far a slide travels, as a fraction of the frame. Mirrored by
 * ``text_overlay.py`` — the two renderers have to agree on the distance or
 * the caption arrives from a different place in the export. */
export const SLIDE_DISTANCE = 0.08

/**
 * Where a transition is at project-second ``time``: how visible the caption
 * is and how far it still has to travel.
 *
 * ``progress`` runs 0 → 1 over the entrance and 1 → 0 over the exit, so a
 * single easing curve covers both and the caller only decides *what* to
 * move. Outside both ramps it is 1 — fully arrived.
 */
export function transitionProgress(text: TextOverlay, time: number): number {
  const span = Math.max(0, text.end - text.start)
  if (span <= 0) return 1
  const half = span / 2

  const inKind = text.transitionIn.kind
  if (inKind !== 'none') {
    const d = Math.min(text.transitionIn.duration, half)
    if (d > 0 && time < text.start + d) {
      return ease(Math.max(0, (time - text.start) / d))
    }
  }
  const outKind = text.transitionOut.kind
  if (outKind !== 'none') {
    const d = Math.min(text.transitionOut.duration, half)
    if (d > 0 && time > text.end - d) {
      return ease(Math.max(0, (text.end - time) / d))
    }
  }
  return 1
}

/** Which of the two transitions is running at ``time`` — they use different
 * directions, so the caller needs to know whose geometry to apply. */
export function activeTransition(text: TextOverlay, time: number): TextTransition | null {
  const span = Math.max(0, text.end - text.start)
  const half = span / 2
  const inD = Math.min(text.transitionIn.duration, half)
  if (text.transitionIn.kind !== 'none' && inD > 0 && time < text.start + inD) {
    return text.transitionIn
  }
  const outD = Math.min(text.transitionOut.duration, half)
  if (text.transitionOut.kind !== 'none' && outD > 0 && time > text.end - outD) {
    return text.transitionOut
  }
  return null
}

/**
 * Linear, and that is a constraint rather than a preference.
 *
 * The export ramps a caption's alpha with ffmpeg's ``fade``, which is
 * linear and has no curve option; the only ways to get an eased alpha there
 * are a per-pixel expression (``geq``, far too slow on a 4K master) or one
 * PNG per frame. Easing here and not there would put a visible difference
 * between the preview and the file — exactly the class of bug this tool
 * keeps paying to avoid. Over the 0,2–1 s a caption transition lasts, the
 * curve is the part nobody misses.
 */
function ease(p: number): number {
  return Math.max(0, Math.min(1, p))
}

/**
 * Inline style for a caption mid-transition, in **percent of the frame**
 * for the translation so it scales with the preview box the way the burn-in
 * scales with the master.
 *
 * Returns the identity style when nothing is running, which keeps the
 * caller free of branches.
 */
export function transitionStyle(
  text: TextOverlay,
  time: number,
): { opacity: number; dx: number; dy: number; scale: number } {
  const active = activeTransition(text, time)
  if (!active) return { opacity: 1, dx: 0, dy: 0, scale: 1 }
  const p = transitionProgress(text, time)
  const away = 1 - p
  switch (active.kind) {
    case 'slide-up':
      return { opacity: p, dx: 0, dy: away * SLIDE_DISTANCE, scale: 1 }
    case 'slide-down':
      return { opacity: p, dx: 0, dy: -away * SLIDE_DISTANCE, scale: 1 }
    case 'slide-left':
      return { opacity: p, dx: away * SLIDE_DISTANCE, dy: 0, scale: 1 }
    case 'slide-right':
      return { opacity: p, dx: -away * SLIDE_DISTANCE, dy: 0, scale: 1 }
    case 'fade':
    case 'none':
    default:
      return { opacity: p, dx: 0, dy: 0, scale: 1 }
  }
}

/**
 * Colour correction over a stretch of the cut.
 *
 * Scoped, on purpose, to **exactly what a CSS ``filter`` can preview**
 * (decision of 08/08/2026): brightness, contrast, saturation and hue. White
 * point, black point, shadows and vignette have no CSS-filter equivalent
 * and stay out until the preview is a WebGL surface — the whole tool's
 * promise is that what you watch is what gets rendered, and a control that
 * only shows up in the export breaks it silently.
 *
 * The ffmpeg side deliberately reproduces the **SVG filter formulas** the
 * browser uses (see ``color_grade.py``) rather than reaching for ``eq``,
 * which works in YUV and would land somewhere near but not on the preview.
 */
export interface ColorGrade {
  id: string
  /** Project seconds on the clips axis, like a caption. */
  start: number
  end: number
  /** Multipliers; 1 leaves the channel alone. */
  brightness: number
  contrast: number
  saturation: number
  /** Hue rotation in degrees, −180…180. */
  hue: number
}

export const DEFAULT_GRADE: Omit<ColorGrade, 'id' | 'start' | 'end'> = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  hue: 0,
}

/** How long a freshly added grade lasts, in seconds. */
export const DEFAULT_GRADE_DURATION_S = 4

/** Ranges the sliders allow. Beyond these the image is destroyed rather
 * than graded, and every extra stop costs preview/export fidelity. */
export const GRADE_LIMITS = {
  brightness: { min: 0.2, max: 2 },
  contrast: { min: 0.2, max: 2 },
  saturation: { min: 0, max: 3 },
  hue: { min: -180, max: 180 },
} as const

/** True when a grade would change nothing — used to skip it in the export
 * filter chain and to grey the tag out. */
export function isNeutralGrade(grade: ColorGrade): boolean {
  return (
    grade.brightness === 1 && grade.contrast === 1 && grade.saturation === 1 && grade.hue === 0
  )
}

/**
 * The grade as a CSS ``filter`` value.
 *
 * Order is load-bearing and mirrored in ``color_grade.py``: brightness →
 * contrast → saturate → hue-rotate. CSS filters compose in written order,
 * and so does the ffmpeg chain, so changing this here without changing it
 * there makes preview and export disagree on anything but neutral values.
 */
export function gradeFilter(grade: ColorGrade): string {
  return [
    `brightness(${String(round3(grade.brightness))})`,
    `contrast(${String(round3(grade.contrast))})`,
    `saturate(${String(round3(grade.saturation))})`,
    `hue-rotate(${String(round3(grade.hue))}deg)`,
  ].join(' ')
}

/** Grades covering a moment, composed left to right. ``none`` when there is
 * nothing to apply, which is the value CSS wants for "don't". */
export function gradeFilterAt(grades: ColorGrade[], time: number): string {
  const active = grades.filter((g) => time >= g.start && time < g.end && !isNeutralGrade(g))
  if (active.length === 0) return 'none'
  return active.map(gradeFilter).join(' ')
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000
}

/**
 * The output frame, as a fraction of the source frame.
 *
 * **One per project, not per clip** (decision of 08/08/2026): a Short has a
 * single framing, and a crop that changed between clips would change the
 * video's shape halfway through. It is what decides the exported aspect
 * ratio, which is why it lives next to the project's name rather than
 * inside the list.
 *
 * Same coordinate system as {@link TextOverlay} — fractions, never pixels —
 * so it means the same thing over a 720p proxy and a 4K master.
 */
export interface CropRect {
  /** Top-left corner. */
  x: number
  y: number
  width: number
  height: number
}

export const FULL_FRAME: CropRect = { x: 0, y: 0, width: 1, height: 1 }

/** Smallest crop we allow, as a fraction of each side. Below this the
 * handles overlap and the export upscales past any useful resolution. */
export const MIN_CROP = 0.1

/** True when the crop is the whole frame — nothing to draw, nothing to
 * apply, and the export can skip the filter entirely. */
export function isFullFrame(crop: CropRect | null): boolean {
  if (!crop) return true
  return (
    Math.abs(crop.x) < 1e-4 &&
    Math.abs(crop.y) < 1e-4 &&
    Math.abs(crop.width - 1) < 1e-4 &&
    Math.abs(crop.height - 1) < 1e-4
  )
}

export interface CropPreset {
  key: string
  label: string
  /**
   * Pixel ratio the rectangle is locked to. ``'source'`` is the video's own
   * shape — resolved per project, since it depends on the footage — and
   * ``null`` is no lock at all.
   */
  ratio: number | 'source' | null
}

/**
 * Aspect presets. **"Original" comes first and is the default**: opening the
 * framing tool on a project that has none should offer the shape the video
 * already is, not an unlocked rectangle. Starting free means the first
 * corner drag silently changes the output's proportions, which is almost
 * never what someone reaching for "Enquadrar" wanted.
 */
export const CROP_PRESETS: readonly CropPreset[] = [
  { key: 'original', label: 'Original', ratio: 'source' },
  { key: 'free', label: 'Livre', ratio: null },
  { key: '9:16', label: '9:16', ratio: 9 / 16 },
  { key: '1:1', label: '1:1', ratio: 1 },
  { key: '4:5', label: '4:5', ratio: 4 / 5 },
  { key: '16:9', label: '16:9', ratio: 16 / 9 },
]

export const DEFAULT_CROP_PRESET = 'original'

/** The locked ratio a preset means for this footage, or ``null`` for free. */
export function cropPresetRatio(key: string, frameAspect: number): number | null {
  const preset = CROP_PRESETS.find((p) => p.key === key)
  if (!preset || preset.ratio === null) return null
  return preset.ratio === 'source' ? frameAspect : preset.ratio
}

/** Shadow presets, in `em`. Black at 0.12 is the old hard-coded value. */
export const SHADOW_SIZES = [
  { label: 'P', value: 0.06 },
  { label: 'M', value: 0.12 },
  { label: 'G', value: 0.22 },
] as const

/**
 * The caption's shadow, built once and used by **both** renderers (the
 * editor inline, ``text_overlay.py`` for the burn-in).
 *
 * ``filter: drop-shadow``, deliberately **not** ``text-shadow``: on a
 * COLRv1 colour font (Honk, Nabla) Chromium paints ``text-shadow`` over
 * the glyph's layer bounds instead of its silhouette, so the halo comes
 * out as a hard black rectangle. ``drop-shadow`` works on the rasterised
 * alpha, colour layers included, so one rule covers every font.
 *
 * Two passes: a tight ring so the glyph survives any background, plus a
 * softer drop for depth.
 */
export function shadowFilter(size: number, color: string): string {
  const rgb = hexToRgb(color)
  return (
    `drop-shadow(0 0 ${String(size)}em rgba(${rgb},0.85)) ` +
    `drop-shadow(0 ${String(size * 0.5)}em ${String(size * 1.67)}em rgba(${rgb},0.7))`
  )
}

/** ``#RRGGBB`` → ``r,g,b``. Anything unparseable falls back to black,
 * which is what a shadow was before it was configurable. */
export function hexToRgb(hex: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match?.[1]) return '0,0,0'
  const n = parseInt(match[1], 16)
  return `${String((n >> 16) & 255)},${String((n >> 8) & 255)},${String(n & 255)}`
}

/** Default caption: centred low, the way a Short usually captions. */
export const DEFAULT_TEXT: Omit<TextOverlay, 'id' | 'start' | 'end'> = {
  text: 'Novo texto',
  x: 0.5,
  y: 0.8,
  size: 0.06,
  fontFamily: 'Montserrat-Bold',
  color: '#FFFFFF',
  background: 'shadow',
  shadowSize: 0.12,
  shadowColor: '#000000',
  transitionIn: DEFAULT_TRANSITION,
  transitionOut: DEFAULT_TRANSITION,
}

/** How long a freshly added caption lasts, in seconds. */
export const DEFAULT_TEXT_DURATION_S = 3

export interface ToolProject {
  id: string
  /** Free text shown in the header. Seeded from the first video's file
   * name, then whatever the user types — no filesystem constraints, it
   * is only normalized when it becomes a download filename. */
  name: string
  videos: ProjectVideo[]
  /** Project-wide and ordered: clips from different videos live in the
   * same list, and that order drives export + the joined video. */
  clips: ProjectClip[]
  /** Captions over the timeline. Ordered by ``start`` for rendering, but
   * order carries no meaning the way the clip list's does. */
  texts: TextOverlay[]
  /** Colour correction ranges, on the same axis as the captions. */
  grades: ColorGrade[]
  /** Output framing, or ``null`` for the whole frame. One per project — see
   * {@link CropRect}. */
  crop: CropRect | null
  createdAt: string
  updatedAt: string
}

/** Captions visible at a given project second. */
export function textsAt(texts: TextOverlay[], time: number): TextOverlay[] {
  return texts.filter((t) => time >= t.start && time < t.end)
}

/** Colour for a clip, derived from its id (see {@link CLIP_COLORS}). */
export function clipColor(clipId: number): ClipColor {
  const index = (((clipId - 1) % CLIP_COLORS.length) + CLIP_COLORS.length) % CLIP_COLORS.length
  return CLIP_COLORS[index] ?? CLIP_COLORS[0]
}

/** What the clip is called in the UI: the user's name, or "Clipe <n>"
 * where n is its 1-based position in the project's list. */
export function clipLabel(clip: ProjectClip, index: number): string {
  const named = clip.name.trim()
  return named || `Clipe ${String(index + 1)}`
}

/** Combining marks left behind by NFKD ("é" → "e" + U+0301). */
const DIACRITICS_RE = /[\u0300-\u036f]/g
const NON_ALNUM_RE = /[^a-z0-9]+/g

/**
 * Mirror of the backend's ``slugify`` (``src/utils/slugify.py``): accents
 * transliterated, everything else collapsed to hyphens, lowercase.
 *
 * Used for names that become file names — the clip's download, the joined
 * video. Display names are never passed through this: the user sees what
 * they typed, the file gets the safe version.
 */
export function normalizeFileStem(name: string, maxLength = 80): string {
  const ascii = name.normalize('NFKD').replace(DIACRITICS_RE, '')
  let slug = ascii.toLowerCase().replace(NON_ALNUM_RE, '-').replace(/^-+|-+$/g, '')
  if (slug.length > maxLength) {
    slug = slug.slice(0, maxLength).replace(/-[^-]*$/, '')
  }
  return slug
}

/** Strip the extension off a file name — the seed for a project name. */
export function stemOf(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot > 0 ? filename.slice(0, dot) : filename
}

/** Random id for projects and videos. ``crypto.randomUUID`` needs a
 * secure context; the fallback keeps the tool working over plain HTTP on
 * a LAN address. */
export function newId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10)
  return `${prefix}-${rand}`
}

/** Next clip id for a project — max + 1, so ids never collide with the
 * colours already on screen even after deletions. */
export function nextClipId(project: ToolProject): number {
  return project.clips.reduce((max, c) => Math.max(max, c.id), 0) + 1
}

export function videoById(project: ToolProject, videoId: string): ProjectVideo | null {
  return project.videos.find((v) => v.id === videoId) ?? null
}

/** Clips belonging to one video, keeping their project-wide position so
 * the timeline can label them with the same number the sidebar shows. */
export function clipsOfVideo(
  project: ToolProject,
  videoId: string,
): { clip: ProjectClip; index: number }[] {
  const out: { clip: ProjectClip; index: number }[] = []
  project.clips.forEach((clip, index) => {
    if (clip.videoId === videoId) out.push({ clip, index })
  })
  return out
}

/**
 * How long a clip runs **on the timeline**, which stopped being
 * ``end - start`` the moment speed and freezes existed.
 *
 * Source footage is divided by the rate; held frames add time that no
 * footage pays for. Every place that used to subtract two source numbers
 * has to come through here instead — that is the whole cost of those two
 * features, and it is why they were built together.
 */
export function clipDuration(clip: ProjectClip): number {
  const source = Math.max(0, clip.end - clip.start)
  const speed = clip.speed > 0 ? clip.speed : 1
  const held = clip.freezes.reduce((sum, f) => sum + Math.max(0, f.duration), 0)
  return source / speed + held
}

/** Total seconds the project's clips add up to on the timeline — hidden
 * ones excluded, since they are in neither the preview nor the export. */
export function totalClipDuration(project: ToolProject): number {
  return project.clips.reduce((sum, c) => (c.hidden ? sum : sum + clipDuration(c)), 0)
}

/** Clips that actually make it into "só os cortes" and into the export. */
export function activeClips(project: ToolProject): ProjectClip[] {
  return project.clips.filter((c) => !c.hidden)
}

/** Freezes sorted and clamped to their clip, which is what the timeline and
 * the export both need — a freeze dragged past a trimmed edge would
 * otherwise split the clip outside its own bounds. */
export function orderedFreezes(clip: ProjectClip): FreezeFrame[] {
  return clip.freezes
    .filter((f) => f.duration > 0 && f.at >= clip.start && f.at <= clip.end)
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id))
}

/**
 * Move a clip, returning a new array.
 *
 * ``insertAt`` is an insertion point in the *original* list — 0 is "before
 * everything", ``clips.length`` is "after everything" — which is what a
 * drop between two rows naturally gives you. Removing the dragged item
 * first shifts every later index down by one, and that's corrected here so
 * call sites don't have to think about it.
 */
export function reorderClips(clips: ProjectClip[], from: number, insertAt: number): ProjectClip[] {
  if (from < 0 || from >= clips.length) return clips
  const next = [...clips]
  const [moved] = next.splice(from, 1)
  if (!moved) return clips
  const target = Math.max(0, Math.min(next.length, insertAt > from ? insertAt - 1 : insertAt))
  if (target === from) return clips
  next.splice(target, 0, moved)
  return next
}
